import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Crown, Eye, Send, Save, RotateCcw } from "lucide-react";

export default function ThankYouControl() {
  const [s, setS] = useState(null);
  const [tourneys, setTourneys] = useState([]);
  const [sel, setSel] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/thankyou/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/tournaments").then((r) => setTourneys((r.data || []).filter((t) => t.status === "complete" && !t.demo))).catch(() => {});
  }, []);
  if (!s) return <div className="text-zinc-600 text-xs animate-pulse">Loading…</div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/thankyou/settings", { subject: s.subject, body: s.body, auto_send: s.auto_send });
      toast.success("Thank-you letter saved");
    } finally { setBusy(false); }
  };
  const doPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/thankyou/preview", { tournament_id: sel || null, subject: s.subject, body: s.body });
      setPreview(data);
    } finally { setBusy(false); }
  };
  const sendNow = async () => {
    if (!sel) return toast.error("Pick a completed tournament first");
    const t = tourneys.find((x) => x.id === sel);
    if (!window.confirm(`Send the gold thank-you letter (email + in-app) to every player from "${t?.name}"?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/thankyou/send/${sel}`);
      toast.success(`Gold letters sent to ${data.sent} player${data.sent === 1 ? "" : "s"} 🏆`);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="thankyou-control" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] text-zinc-500 max-w-lg">
          After every completed tournament, each player gets this letter by email AND as a gold-framed transmission in the app — personalized with their medal, national PBR standing, next-goal push and the gallery link.
        </p>
        <label className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest cursor-pointer" data-testid="ty-autosend-label">
          <input type="checkbox" data-testid="ty-autosend-toggle" checked={!!s.auto_send}
            onChange={(e) => setS({ ...s, auto_send: e.target.checked })} className="accent-yellow-500 w-4 h-4" />
          <span className={s.auto_send ? "text-yellow-400" : "text-zinc-500"}>Auto-send when a tournament completes</span>
        </label>
      </div>

      <div>
        <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Email / letter subject</label>
        <input data-testid="ty-subject-input" value={s.subject} onChange={(e) => setS({ ...s, subject: e.target.value })}
          className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-yellow-500" />
      </div>
      <div>
        <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Letter body — click a chip to add its placeholder</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {s.placeholders.map((p) => (
            <button key={p} data-testid={`ty-chip-${p.replace(/[{}]/g, "")}`} onClick={() => setS({ ...s, body: s.body + (s.body.endsWith("\n") || !s.body ? "" : " ") + p })}
              className="border border-yellow-500/40 text-yellow-400 text-[10px] font-mono px-2 py-1 hover:bg-yellow-500/10 transition-colors">{p}</button>
          ))}
        </div>
        <textarea data-testid="ty-body-input" rows={14} value={s.body} onChange={(e) => setS({ ...s, body: e.target.value })}
          className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-zinc-200 leading-relaxed focus:outline-none focus:border-yellow-500 font-mono" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="ty-save-btn" disabled={busy} onClick={save}
          className="flex items-center gap-1.5 bg-yellow-500 text-black text-[10px] font-black uppercase tracking-widest px-4 py-2.5 hover:bg-yellow-400 disabled:opacity-50">
          <Save className="w-3.5 h-3.5" /> Save Letter
        </button>
        <button data-testid="ty-reset-btn" disabled={busy} onClick={() => setS({ ...s, subject: s.default_subject, body: s.default_body })}
          className="flex items-center gap-1.5 border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-3 py-2.5 hover:border-zinc-500">
          <RotateCcw className="w-3.5 h-3.5" /> Restore Default
        </button>
        <span className="mx-2 h-6 w-px bg-pb-border" />
        <select data-testid="ty-tourney-select" value={sel} onChange={(e) => setSel(e.target.value)}
          className="bg-pb-elevated border border-pb-border px-2 py-2.5 text-xs text-white focus:outline-none max-w-[240px]">
          <option value="">Pick a completed tournament…</option>
          {tourneys.map((t) => <option key={t.id} value={t.id}>{`${t.name} — ${t.date}${t.thank_you_sent_at ? " ✓ sent" : ""}`}</option>)}
        </select>
        <button data-testid="ty-preview-btn" disabled={busy} onClick={doPreview}
          className="flex items-center gap-1.5 border border-pb-cyan/60 text-pb-cyan text-[10px] font-black uppercase tracking-widest px-3 py-2.5 hover:bg-pb-cyan/10 disabled:opacity-50">
          <Eye className="w-3.5 h-3.5" /> Preview
        </button>
        <button data-testid="ty-send-btn" disabled={busy || !sel} onClick={sendNow}
          className="flex items-center gap-1.5 bg-pb-red text-black text-[10px] font-black uppercase tracking-widest px-4 py-2.5 hover:bg-[#BEF264] disabled:opacity-40">
          <Send className="w-3.5 h-3.5" /> Send / Resend Now
        </button>
      </div>

      {preview && (
        <div className="border-2 border-yellow-500/70 bg-black/70 p-1 shadow-[0_0_24px_rgba(212,175,55,0.25)]" data-testid="ty-preview-panel">
          <div className="border border-yellow-500/40 p-5">
            <div className="text-center mb-4">
              <Crown className="w-7 h-7 text-yellow-400 mx-auto mb-1" />
              <div className="text-[9px] font-black uppercase tracking-[0.35em] text-yellow-400">Gold Letter Preview</div>
              <div className="font-display font-black uppercase text-lg mt-1 text-white">{preview.subject}</div>
            </div>
            <div className="whitespace-pre-line text-sm text-zinc-200 leading-relaxed" style={{ fontFamily: "Georgia, serif" }}>{preview.body}</div>
          </div>
        </div>
      )}
    </div>
  );
}
